import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api, { formatApiError } from "@/lib/api";
import {
  PlayCircle,
  CheckCircle,
  Stack,
  ArrowRight,
  Clock,
  CaretDown,
  CaretUp,
} from "@phosphor-icons/react";

const DEFAULT_THUMB =
  "https://images.pexels.com/photos/18075549/pexels-photo-18075549.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function CourseDetail() {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState({});
  const [enrolled, setEnrolled] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openModules, setOpenModules] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const [courseRes, enrolRes, progRes] = await Promise.all([
          api.get(`/courses/${courseId}`),
          api.get("/enrollments"),
          api.get(`/progress/${courseId}`).catch(() => ({ data: {} })),
        ]);
        setCourse(courseRes.data);
        setEnrolled(enrolRes.data.some((e) => e.course_id === courseId));
        setProgress(progRes.data || {});
        // Open all modules by default
        const o = {};
        (courseRes.data.modules || []).forEach((m) => (o[m.id] = true));
        setOpenModules(o);
      } catch (e) {
        setError(formatApiError(e, "Could not load course."));
      } finally {
        setLoading(false);
      }
    })();
  }, [courseId]);

  const stats = useMemo(() => {
    if (!course) return { total: 0, done: 0, percent: 0 };
    const total = (course.modules || []).reduce((s, m) => s + (m.lessons?.length || 0), 0);
    const done = Object.values(progress).filter((p) => p.completed).length;
    return { total, done, percent: total ? Math.round((done / total) * 100) : 0 };
  }, [course, progress]);

  const handleEnroll = async () => {
    try {
      await api.post(`/enroll/${courseId}`);
      setEnrolled(true);
    } catch (e) {
      setError(formatApiError(e, "Could not enrol."));
    }
  };

  const handleStart = () => {
    if (!course) return;
    // Flatten all lessons in order
    const allLessons = [];
    for (const m of course.modules || []) {
      for (const l of m.lessons || []) allLessons.push(l);
    }
    if (allLessons.length === 0) return;

    // 1. Find the lesson the learner most recently interacted with
    let lastWatched = null;
    let lastTime = "";
    for (const l of allLessons) {
      const p = progress[l.id];
      if (p && p.last_watched_at && p.last_watched_at > lastTime) {
        lastTime = p.last_watched_at;
        lastWatched = l;
      }
    }

    // 2. If last-watched is complete, jump to the NEXT lesson after it.
    //    Otherwise resume on it. If no progress yet, start from the very first.
    let target = lastWatched || allLessons[0];
    if (lastWatched && progress[lastWatched.id]?.completed) {
      const idx = allLessons.findIndex((l) => l.id === lastWatched.id);
      if (idx >= 0 && idx < allLessons.length - 1) {
        target = allLessons[idx + 1];
      }
    }
    if (target) navigate(`/courses/${courseId}/lesson/${target.id}`);
  };

  if (loading) {
    return (
      <AppLayout title="Loading..." eyebrow="Course">
        <div className="text-sm text-[#8A8A85]">Loading course...</div>
      </AppLayout>
    );
  }
  if (error || !course) {
    return (
      <AppLayout title="Course" eyebrow="Error">
        <div className="text-sm text-[#993333]">{error || "Course not found."}</div>
      </AppLayout>
    );
  }

  return (
    <AppLayout title={course.title} eyebrow={course.category || "Course"}>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="aspect-[16/9] bg-[#EAEAE6] mb-6 overflow-hidden border border-[#EAEAE6]">
            <img
              src={course.thumbnail_url || DEFAULT_THUMB}
              alt={course.title}
              className="w-full h-full object-cover"
            />
          </div>

          <div className="mb-8">
            <div className="flex flex-wrap items-center gap-3 text-xs uppercase tracking-[0.15em] font-bold text-[#8A8A85] mb-4">
              <span className="px-2 py-1 bg-[#EAEAE6]">{course.level || "Beginner"}</span>
              <span className="flex items-center gap-1.5">
                <Stack size={14} /> {course.modules?.length || 0} modules
              </span>
              <span className="flex items-center gap-1.5">
                <Clock size={14} /> {stats.total} lessons
              </span>
            </div>
            <p className="text-base text-[#3D3D38] leading-relaxed">
              {course.description || "No description provided."}
            </p>
          </div>

          {/* Curriculum */}
          <div className="border border-[#EAEAE6]">
            <div className="px-5 py-4 border-b border-[#EAEAE6] bg-[#F4F4F0] flex items-center justify-between">
              <div className="font-heading font-bold tracking-tight">Curriculum</div>
              <div className="flex items-center gap-3">
                {(course.modules || []).length > 1 && (
                  <div className="flex items-center text-xs">
                    <button
                      onClick={() => {
                        const o = {};
                        (course.modules || []).forEach((m) => (o[m.id] = true));
                        setOpenModules(o);
                      }}
                      data-testid="curriculum-expand-all"
                      className="px-2.5 py-1 border border-[#D8D8D2] rounded-l hover:border-[#1C1C19] text-[#5C5C58] hover:text-[#1C1C19] transition-colors"
                    >
                      Expand all
                    </button>
                    <button
                      onClick={() => setOpenModules({})}
                      data-testid="curriculum-collapse-all"
                      className="px-2.5 py-1 border border-l-0 border-[#D8D8D2] rounded-r hover:border-[#1C1C19] text-[#5C5C58] hover:text-[#1C1C19] transition-colors"
                    >
                      Collapse all
                    </button>
                  </div>
                )}
                <div className="eyebrow">{stats.total} lessons</div>
              </div>
            </div>
            {(course.modules || []).length === 0 && (
              <div className="p-6 text-sm text-[#8A8A85]">No modules added yet.</div>
            )}
            {(course.modules || []).map((m, mIdx) => {
              const opened = openModules[m.id];
              return (
                <div key={m.id} className="border-b border-[#EAEAE6] last:border-b-0">
                  <button
                    onClick={() => setOpenModules({ ...openModules, [m.id]: !opened })}
                    className="w-full flex items-center justify-between px-5 py-4 hover:bg-[#F4F4F0] text-left"
                    data-testid={`module-toggle-${m.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-xs text-[#A8A8A1]">
                        {String(mIdx + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <div className="font-heading font-semibold tracking-tight">{m.title}</div>
                        {m.description && <div className="text-xs text-[#8A8A85] mt-0.5">{m.description}</div>}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-[#8A8A85]">
                      <span>{m.lessons?.length || 0} lessons</span>
                      {opened ? <CaretUp size={16} /> : <CaretDown size={16} />}
                    </div>
                  </button>
                  {opened && (
                    <div className="border-t border-[#EAEAE6] bg-white">
                      {(m.lessons || []).map((l) => {
                        const p = progress[l.id];
                        const done = p?.completed;
                        return (
                          <Link
                            key={l.id}
                            to={enrolled ? `/courses/${courseId}/lesson/${l.id}` : "#"}
                            onClick={(e) => {
                              if (!enrolled) {
                                e.preventDefault();
                                handleEnroll().then(() => navigate(`/courses/${courseId}/lesson/${l.id}`));
                              }
                            }}
                            data-testid={`lesson-link-${l.id}`}
                            className="flex items-center justify-between px-5 py-3 border-t border-[#F4F4F0] first:border-t-0 hover:bg-[#F4F4F0] text-sm"
                          >
                            <div className="flex items-center gap-3">
                              {done ? (
                                <CheckCircle size={20} weight="fill" color="#4A6B53" />
                              ) : (
                                <PlayCircle size={20} weight="duotone" color="#E25822" />
                              )}
                              <span className={done ? "text-[#8A8A85] line-through" : "text-[#1C1C19]"}>{l.title}</span>
                            </div>
                            {l.duration_seconds > 0 && (
                              <span className="text-xs text-[#A8A8A1] font-mono">
                                {Math.round(l.duration_seconds / 60)}m
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Sidebar */}
        <aside className="lg:col-span-1">
          <div className="border border-[#EAEAE6] bg-white p-6 sticky top-24">
            <div className="eyebrow mb-2">Progress</div>
            <div className="font-heading text-5xl font-semibold tracking-tight mb-1">{stats.percent}%</div>
            <div className="text-sm text-[#5C5C58] mb-4">
              {stats.done} of {stats.total} lessons completed
            </div>
            <div className="h-1.5 progress-track mb-6">
              <div
                className="h-full bg-[#E25822] transition-all duration-500"
                style={{ width: `${stats.percent}%` }}
                data-testid="course-progress-bar"
              />
            </div>
            {enrolled ? (
              <button
                onClick={handleStart}
                className="w-full bg-[#E25822] hover:bg-[#C44E1A] text-white font-medium py-3 inline-flex items-center justify-center gap-2"
                data-testid="course-continue-button"
              >
                {stats.done > 0 ? "Continue learning" : "Start learning"}
                <ArrowRight size={16} weight="bold" />
              </button>
            ) : (
              <button
                onClick={handleEnroll}
                className="w-full bg-[#E25822] hover:bg-[#C44E1A] text-white font-medium py-3 inline-flex items-center justify-center gap-2"
                data-testid="course-enroll-button"
              >
                Enrol in this course <ArrowRight size={16} weight="bold" />
              </button>
            )}
            <div className="mt-6 pt-6 border-t border-[#EAEAE6] text-xs text-[#8A8A85] space-y-2">
              <div className="flex justify-between">
                <span>CATEGORY</span>
                <span className="font-semibold text-[#1C1C19]">{course.category || "General"}</span>
              </div>
              <div className="flex justify-between">
                <span>LEVEL</span>
                <span className="font-semibold text-[#1C1C19]">{course.level || "Beginner"}</span>
              </div>
              <div className="flex justify-between">
                <span>MODULES</span>
                <span className="font-semibold text-[#1C1C19]">{course.modules?.length || 0}</span>
              </div>
              <div className="flex justify-between">
                <span>LESSONS</span>
                <span className="font-semibold text-[#1C1C19]">{stats.total}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </AppLayout>
  );
}
